import {
  CheckboxInput,
  ErrorMessage,
  SubmitButton,
} from '@retainerclub/common';
import { useFormValues, usePolicy } from '@retainerclub/hooks';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import DiscountCodeInput from './DiscountCodeInput';

function CheckoutCartActions({
  commercialInputsLocked,
  ctError,
  ctLoading,
  deliveryUpdateState,
  error,
  loading,
  mutationErrors,
  recoveryError,
  recoveryLoading,
  requiresPaymentReconciliation,
  retainedAttempt,
  taxQuote,
}) {
  const { setPolicyAndOpenModal } = usePolicy();
  const {
    formState: { isSubmitting, isLoading },
  } = useFormContext();
  const { acceptedTerms } = useFormValues();
  const retainedPaymentStatus = retainedAttempt?.status || null;
  const quoteBlocksSubmit = !(retainedPaymentStatus || taxQuote?.isReady);
  const recoveryBlocksSubmit =
    (!retainedAttempt && recoveryLoading) ||
    (!retainedAttempt && Boolean(recoveryError)) ||
    Boolean(retainedAttempt?.orderId) ||
    retainedAttempt?.missingQuotedEvidence ||
    ['pending', 'reconciliation_required'].includes(retainedPaymentStatus) ||
    (retainedPaymentStatus === 'ambiguous' &&
      retainedAttempt?.recoveryMode === 'legacy');

  return (
    <div className="border-gray-200 border-t px-4 py-6 sm:px-6">
      {error ? (
        <div className="col-span-full my-4">
          <ErrorMessage error={error} />
        </div>
      ) : null}

      {ctError ? (
        <div className="col-span-full my-4">
          <ErrorMessage error={ctError} />
        </div>
      ) : null}

      {mutationErrors ? (
        <div className="col-span-full my-4">
          <ErrorMessage errorMessages={mutationErrors} />
        </div>
      ) : null}

      <div className="col-span-full mb-6">
        <DiscountCodeInput disabled={commercialInputsLocked} />
      </div>

      <div className="col-span-full mb-6">
        <CheckboxInput
          name="acceptedTerms"
          label="Terms and Conditions"
          description={
            <span>
              By checking this box, you agree to Retainer Club&apos;s&nbsp;
              <button
                type="button"
                className="text-rcprimary-400 hover:text-rcprimary-300"
                onClick={() => setPolicyAndOpenModal('terms')}
              >
                Terms and Conditions
              </button>
              ,&nbsp; shipping and return policies.
            </span>
          }
        />
      </div>

      <SubmitButton
        isLoading={
          isLoading || loading || ctLoading || deliveryUpdateState.loading
        }
        isSubmitting={isSubmitting}
        disabled={
          !acceptedTerms ||
          quoteBlocksSubmit ||
          recoveryBlocksSubmit ||
          requiresPaymentReconciliation ||
          deliveryUpdateState.blocked
        }
        label={
          retainedPaymentStatus === 'paid' ? 'Finish Order' : 'Place Order'
        }
        full
        notRounded
      />
    </div>
  );
}

CheckoutCartActions.propTypes = {
  commercialInputsLocked: PropTypes.bool.isRequired,
  ctError: PropTypes.object,
  ctLoading: PropTypes.bool.isRequired,
  deliveryUpdateState: PropTypes.shape({
    blocked: PropTypes.bool.isRequired,
    loading: PropTypes.bool.isRequired,
  }).isRequired,
  error: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  mutationErrors: PropTypes.array,
  recoveryError: PropTypes.object,
  recoveryLoading: PropTypes.bool.isRequired,
  requiresPaymentReconciliation: PropTypes.bool.isRequired,
  retainedAttempt: PropTypes.shape({
    missingQuotedEvidence: PropTypes.bool,
    orderId: PropTypes.string,
    recoveryMode: PropTypes.string,
    status: PropTypes.string.isRequired,
  }),
  taxQuote: PropTypes.shape({ isReady: PropTypes.bool }),
};

export default CheckoutCartActions;
